/**
 * Rótulos, situações e textos da Prospecção (admin/prospects/Prospects.php).
 */

import { loadTemplates, renderTemplate } from './messageTemplates';

export const CATEGORY_OPTIONS = [
  { value: 'physio', label: 'Fisioterapia' },
  { value: 'osteopathy', label: 'Osteopatia' },
  { value: 'pilates', label: 'Pilates' },
  { value: 'nutrition', label: 'Nutrição' },
  { value: 'aesthetics', label: 'Estética' },
  { value: 'beauty', label: 'Beleza' },
  { value: 'psychology', label: 'Psicologia' },
  { value: 'therapies', label: 'Terapias' },
  { value: 'speech', label: 'Fonoaudiologia' },
  { value: 'medicine', label: 'Medicina' },
  { value: 'dentistry', label: 'Odontologia' },
  { value: 'other', label: 'Outro' },
];

export const STATUS_OPTIONS = [
  { value: 'new', label: 'Novo', tone: 'grey' },
  { value: 'contacted', label: 'Contatado', tone: 'blue' },
  { value: 'replied', label: 'Respondeu', tone: 'orange' },
  { value: 'no_reply', label: 'Sem resposta', tone: 'grey' },
  { value: 'trial', label: 'Em teste', tone: 'green' },
  { value: 'customer', label: 'Cliente', tone: 'green' },
  { value: 'not_interested', label: 'Sem interesse', tone: 'red' },
];

/**
 * Fila do dia (list.php?view=today, coluna today_group), na ordem em que vale agir.
 * Regras no backend (Prospects::todayGroup): 3 dias com uma mensagem = retorno;
 * 7 dias com duas ou mais = encerrar.
 */
export const TODAY_GROUPS = [
  { value: 'replied', title: 'Responderam', tone: 'orange', description: 'Responda pelo celular do número da Gabriela e marque a situação (Em teste, Sem interesse...) no contato.' },
  { value: 'follow_up', title: 'Retorno vencido', tone: 'blue', description: 'Uma mensagem, 3 dias ou mais sem resposta. Sai o texto "Retorno" para os marcados.' },
  { value: 'close', title: 'Encerrar', tone: 'grey', description: 'Já receberam o retorno e passaram 7 dias sem resposta. Viram "Sem resposta" (diferente de "Sem interesse": podem ser procurados de novo mais tarde).' },
  { value: 'new', title: 'Novos', tone: 'green', description: 'Ainda não receberam nada. Primeira mensagem para os marcados, dentro da cota do dia.' },
];

/** Origem do contato (coluna source). "referral" = indicado por uma conta pelo Indique e ganhe. */
export const SOURCE_OPTIONS = [
  { value: 'manual', label: 'Manual' },
  { value: 'google_maps', label: 'Google Maps' },
  { value: 'instagram', label: 'Instagram' },
  { value: 'referral', label: 'Indicação' },
];

export const CHANNEL_OPTIONS = [
  { value: 'whatsapp', label: 'WhatsApp' },
  { value: 'instagram', label: 'Instagram' },
  { value: 'email', label: 'E-mail' },
];

export const NOTE_MAX_LENGTH = 1000;
export const MESSAGE_MAX_LENGTH = 2000;
export const EMAIL_SUBJECT = 'Uma pergunta sobre a sua agenda';

/** O MUI abre o menu do select em z-index 1300; o ConfirmDialog fica em 9999. */
export const SELECT_ABOVE_DIALOG = { MenuProps: { sx: { zIndex: 10000 } } };

/**
 * @param {Array<{ value: string, label: string }>} options
 * @param {string} value
 * @returns {string}
 */
export function labelOf(options, value) {
  return options.find((item) => item.value === value)?.label || value || '—';
}

/**
 * @param {string} status
 * @returns {{ value: string, label: string, tone: string }}
 */
export function statusOption(status) {
  return STATUS_OPTIONS.find((item) => item.value === status) || STATUS_OPTIONS[0];
}

/** Para quem é o sistema e como se chama o atendimento, por categoria */
const AUDIENCES = {
  physio: ['fisioterapeutas', 'sessões'],
  osteopathy: ['osteopatas', 'sessões'],
  pilates: ['studios de pilates', 'aulas'],
  nutrition: ['nutricionistas', 'consultas'],
  aesthetics: ['clínicas de estética', 'atendimentos'],
  beauty: ['salões e studios de beleza', 'horários'],
  psychology: ['psicólogos', 'sessões'],
  therapies: ['terapeutas', 'sessões'],
  speech: ['fonoaudiólogos', 'sessões'],
  medicine: ['consultórios médicos', 'consultas'],
  dentistry: ['consultórios odontológicos', 'consultas'],
};
const DEFAULT_AUDIENCE = ['profissionais de saúde e bem-estar', 'atendimentos'];

/** @returns {string} "Bom dia", "Boa tarde" ou "Boa noite" pela hora de quem envia */
function greeting() {
  const hour = new Date().getHours();
  if (hour < 12) return 'Bom dia';
  return hour < 18 ? 'Boa tarde' : 'Boa noite';
}

const SITE_REFS = { conversa: 'a', link: 'b', retorno: 'c' };

/**
 * Site com link curto da versão (?ref=a conversa, ?ref=b link, ?ref=c retorno): na
 * entrada vira a UTM prospeccao_<versão> (SHORT_REFS em utils/signupAttribution.js), e o
 * cadastro guarda de qual texto a pessoa veio.
 * @param {string} variant
 * @returns {string}
 */
export function prospectSiteLink(variant) {
  return `https://www.gabriela.app.br/?ref=${SITE_REFS[variant] || 'a'}`;
}

/**
 * Versões do texto (coluna variant de platform_prospect_messages). As duas primeiras são
 * a primeira mensagem, em teste A/B: "conversa" pergunta antes e manda o link só para quem
 * responder; "link" já vai com ele. "retorno" é o lembrete para quem não respondeu.
 */
export const MESSAGE_VARIANTS = [
  { value: 'conversa', label: 'Puxar conversa' },
  { value: 'link', label: 'Direto com link' },
  { value: 'retorno', label: 'Retorno' },
];
const FIRST_MESSAGE_VARIANTS = MESSAGE_VARIANTS.slice(0, 2);

/**
 * Quem já recebeu mensagem abre no Retorno. Na primeira, alterna as versões pelo id do
 * contato, para o teste sair meio a meio sem o admin precisar lembrar qual mandou por último.
 * @param {{ id: number, messages_count?: number }} prospect
 * @returns {string}
 */
export function defaultVariant(prospect) {
  if (Number(prospect?.messages_count) > 0) return 'retorno';
  return Number(prospect?.id) % 2 === 0 ? 'conversa' : 'link';
}

/** Envio em lote pelo número da Gabriela: limite por vez e intervalo sorteado entre um e outro */
export const BULK_MAX = 30;
export const BULK_INTERVAL_SECONDS = [10, 15];

export const BULK_DISTRIBUTIONS = [
  { value: 'split', label: 'Distribuir A/B' },
  { value: 'conversa', label: 'Só puxar conversa' },
  { value: 'link', label: 'Só direto com link' },
  { value: 'retorno', label: 'Retorno' },
];

/**
 * Versão de cada contato do lote. "split" alterna as duas primeiras pela ordem, para
 * sair meio a meio.
 * @param {Array<Object>} prospects
 * @param {string} distribution 'split' | 'conversa' | 'link' | 'retorno'
 * @returns {Array<{ prospect: Object, variant: string }>}
 */
export function assignVariants(prospects, distribution) {
  return prospects.map((prospect, index) => ({
    prospect,
    variant: distribution === 'split' ? FIRST_MESSAGE_VARIANTS[index % 2].value : distribution,
  }));
}

/**
 * Mensagem para um contato, a partir do texto da versão (messageTemplates.js, editável
 * na tela). No envio um a um o admin ainda pode editar antes de mandar.
 * @param {{ category: string }} prospect
 * @param {string} [variant] 'conversa' | 'link' | 'retorno'
 * @returns {string}
 */
export function defaultMessage(prospect, variant = 'conversa') {
  const [audience, appointments] = AUDIENCES[prospect?.category] || DEFAULT_AUDIENCE;
  const templates = loadTemplates();
  return renderTemplate(templates[variant] || templates.conversa, {
    saudacao: greeting(),
    publico: audience,
    atendimentos: appointments,
    link: prospectSiteLink(variant),
  });
}

/**
 * Link que abre a conversa no WhatsApp com o texto pronto. Número sem DDI ganha o 55.
 * @param {string} phone
 * @param {string} text
 * @returns {string|null}
 */
export function whatsappLink(phone, text) {
  let digits = String(phone || '').replace(/\D+/g, '');
  if (!digits) return null;
  if (digits.length <= 11) digits = `55${digits}`;
  return `https://wa.me/${digits}?text=${encodeURIComponent(text)}`;
}

/**
 * Direct do Instagram (não aceita texto pronto: a tela copia antes de abrir).
 * @param {string} username
 * @returns {string|null}
 */
export function instagramLink(username) {
  return username ? `https://ig.me/m/${encodeURIComponent(username)}` : null;
}

/**
 * @param {string} username
 * @returns {string|null}
 */
export function instagramProfileLink(username) {
  return username ? `https://instagram.com/${encodeURIComponent(username)}` : null;
}

/**
 * @param {string} email
 * @param {string} text
 * @returns {string|null}
 */
export function emailLink(email, text) {
  if (!email) return null;
  return `mailto:${email}?subject=${encodeURIComponent(EMAIL_SUBJECT)}&body=${encodeURIComponent(text)}`;
}
